import {inject, observer} from "mobx-react";
import React, {useContext} from "react";
import {RouteComponentProps, useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {IInjectedRootStore, IObservedRootStore, PatientStore} from "stores";
import {
    AuthContext,
    IAuthContextProps,
    LoadingMeter, PatientStatementDetails,
    ProviderPatientStatement
} from "@enter/core";
import {IProviderMessagingContextProps, PageLayout, ProviderMessagingContext} from "components";
import {DialogContext, IDialogContextProps} from "services";
import _ from "lodash";
import {Fade} from "@mui/material";
import * as S from "./patient-statement.styles";
import {ActionItem, CardAction} from "@enter/core/src/ui/claim-details/cards/cards";


interface IMatchParams {
    patientId: string;
}

interface IPatientDetailProps extends RouteComponentProps<IMatchParams>, IInjectedRootStore {
    authContext: IAuthContextProps;
    messagingContext: IProviderMessagingContextProps;
    dialogContext: IDialogContextProps;
}

interface IPatientDetailState {
    patientStatement: ProviderPatientStatement | undefined;
    loading: boolean;
    printMode: boolean;
}

// interface StatementRow {
//     id: string,
//     date?: string,
//     text?: string,
//     charge?: string,
//     credit?: string,
//     balance?: string
// }

@observer
class PatientStatementPage extends React.Component<IPatientDetailProps, IPatientDetailState> {
    private patientId: string;

    constructor(props: IPatientDetailProps) {
        super(props);
        this.patientId = this.props.match.params.patientId;
        if (this.patientId === undefined) {
            throw Error('No such patient');
        }
        this.state = {
            patientStatement: undefined,
            loading: false,
            printMode: false
        }
    }

    private get patientStore(): PatientStore {
        return this.props.rootStore.patientStore;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    public componentDidMount(): void {
        window.scrollTo(0, 0);
        this.fetchData();
        window.addEventListener('afterprint', () => this.handleAfterPrint());
        this.props.rootStore.layoutStore.toggleSidebar(true);
    }

    public componentDidUpdate(prevProps, prevState) {
        if (!prevState.printMode && this.state.printMode) {
            window.print();
        }
    }

    public handleAfterPrint(): void {
        this.setState(state => ({
            ...state,
            printMode: false,
        }));
        console.log("print mode set to false");
    }

    public componentWillUnmount() {
        window.removeEventListener('afterprint', this.handleAfterPrint);
    }

    private fetchData = async (): Promise<void> => {
        if (!this.state.patientStatement || !this.state.patientStatement) {
            try {
                this.setState({ loading: true });

                // query for claim in case already available
                const patientStatement = await this.patientStore.loadPatientStatement(this.patientId);

                this.setState(state => ({
                    ...state,
                    patientStatement,
                }));

            } catch (err) {
                if (err && err.payload) {
                    const { payload } = err;
                    if (payload.code === 'err.application_access_token_has_expired') {
                        // NB: continue silently
                    } else if (payload.code === 'err.access_denied' || payload.code === 'err.claim_not_found') {
                        if (this.props.history) {
                            this.props.history.push(`/claims`);
                        }
                    } else {
                        this.dialogContext.showError(err);
                    }
                } else if (_.get(err, 'name') === 'AbortError' || _.get(err, 'name') === 'TypeError') {
                    // NB: continue silently
                    // AbortError is from cancelled requests;
                    // TypeError from CORS errors (failed to fetch);
                } else {
                    this.dialogContext.showError(err);
                }
            } finally {
                this.setState({ loading: false });
            }
        }
    };

    private readonly renderLoader = (): React.ReactElement => (
        <Fade in={true}>
            <S.Loader>
                <LoadingMeter />
            </S.Loader>
        </Fade>
    );

    // private getDateString(fromDos?: string, throughDos?: string): string {
    //     if(!fromDos && !throughDos) {
    //         return '';
    //     } else if(fromDos && !throughDos) {
    //         return fromDos;
    //     } else if(!fromDos && throughDos) {
    //         return throughDos;
    //     } else if(fromDos && throughDos && fromDos === throughDos) {
    //         return fromDos;
    //     } else {
    //         return fromDos + ' - ' + throughDos;
    //     }
    // }
    //
    // private getProviderString(renderingProviders?: Array<ProviderPatientClaimStatementSimpleProviderInfo>): string {
    //     if(renderingProviders) {
    //         let renderingProviderString: string = '';
    //         for(const renderingProvider of renderingProviders) {
    //             if(renderingProviderString.length > 0) {
    //                 renderingProviderString+= ', ';
    //             }
    //             renderingProviderString+= ((renderingProvider.firstName) + ' ' + (renderingProvider.lastName));
    //         }
    //         return renderingProviderString;
    //     } else {
    //         return '';
    //     }
    // }
    //
    // private getCurrentDate(): string {
    //     const today = new Date();
    //
    //     const month = String(today.getMonth() + 1).padStart(2, '0');  // months are 0-indexed in JavaScript
    //     const day = String(today.getDate()).padStart(2, '0');
    //     const year = today.getFullYear();
    //
    //     return `${month}/${day}/${year}`;
    // }

    private readonly renderPage = (printMode: boolean, patientStatement: ProviderPatientStatement): React.ReactElement => {
        return <PageLayout>
            {<style>
                {`@media print {#root>section{display: none;}}`}
                {`@media print {#printStatementBtnContainer{display: none;}}`}
                {`@media print {#root>main{max-height: 100% !important; height: 100% !important;}}`}
            </style>}
            {this.renderHeader()}
            {this.renderStatement(patientStatement)}
        </PageLayout>
    }

    private printPage = (): void => {
        this.setState(state => ({
            ...state,
            printMode: true,
        }));
    }

    private readonly renderHeader = () => {
        return <S.StyledPageLayoutRow>
            <table id="printStatementBtnContainer">
                <tr>
                    <td style={{textAlign: 'right'}}>
                        <S.LinkButton variant="primary" onClick={this.printPage} disabled={this.state.printMode}>
                            Print Statement
                        </S.LinkButton>
                    </td>
                </tr>
            </table>
        </S.StyledPageLayoutRow>
    }

    private readonly renderStatement = (patientStatement: ProviderPatientStatement): React.ReactElement => {
        return <PatientStatementDetails patientStatement={patientStatement} printMode={this.state.printMode} dialogContext={this.props.dialogContext} showActions={false}/>;
    }

    // private readonly renderStatement = (patientStatement: ProviderPatientStatement): React.ReactElement => {
    //
    //     const statementRows: StatementRow[] = [];
    //
    //     if(patientStatement.paymentSources) {
    //         for (const paymentSource of patientStatement.paymentSources) {
    //             if(paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount !== '0.00') {
    //                 const text = paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount && parseInt(paymentSource.unallocatedAmount.amount.replace('.', '')) > 0 ? 'Patient Payment (Unallocated)' : 'Patient Refund (Unallocated)';
    //
    //                 let negatedAmount = paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount ? paymentSource.unallocatedAmount.amount : '0.00';
    //                 if(negatedAmount != '0.00') {
    //                     if(negatedAmount.indexOf('-') > -1) {
    //                         negatedAmount = negatedAmount.replace("-", "");
    //                     } else {
    //                         negatedAmount = '-' + negatedAmount;
    //                     }
    //                 }
    //
    //                 statementRows.push({
    //                     id: 'patientPayment' + paymentSource.id,
    //                     text: text,
    //                     date: paymentSource.paymentDate,
    //                     credit: paymentSource.unallocatedAmount ? paymentSource.unallocatedAmount.amount : '0.00',
    //                     balance: negatedAmount
    //                 });
    //             }
    //         }
    //     }
    //
    //     if(patientStatement.claims) {
    //         for (const patientStatementClaim of patientStatement.claims) {
    //             const renderingProviderString: string = this.getProviderString(patientStatementClaim.renderingProviders);
    //             statementRows.push({
    //                 id: 'claimDetails' + patientStatementClaim.externalId,
    //                 date: this.getDateString(patientStatementClaim.fromDos, patientStatementClaim.throughDos),
    //                 text: 'Claim ' + patientStatementClaim.externalId + (patientStatementClaim.pendingAdjudication ? ' (Pending Adjudication)' : '') + (renderingProviderString.length > 0 ? ', Provider(s): ' + renderingProviderString : '')
    //             });
    //
    //             if(patientStatementClaim.serviceLines) {
    //                 for (const serviceLine of patientStatementClaim.serviceLines) {
    //                     let descriptionText: string = '';
    //                     if(serviceLine.procedure) {
    //                         descriptionText+= serviceLine.procedure;
    //                     }
    //                     if(serviceLine.description) {
    //                         if(descriptionText.length > 0) {
    //                             descriptionText+= ' ';
    //                         }
    //                         descriptionText+= serviceLine.description;
    //                     }
    //                     statementRows.push({
    //                         id: 'claim' + patientStatementClaim.externalId + serviceLine.id,
    //                         text: descriptionText,
    //                         charge: serviceLine.chargeAmount ? serviceLine.chargeAmount.amount : ''
    //                     });
    //                 }
    //             }
    //
    //             if(patientStatementClaim.discountLines) {
    //                 for(const discountPayment of patientStatementClaim.discountLines) {
    //                     statementRows.push({
    //                         id: 'discountPayment' + discountPayment.id,
    //                         text: 'Discount',
    //                         date: discountPayment.paymentDate,
    //                         credit: discountPayment.amount ? discountPayment.amount.amount : '0.00'
    //                     });
    //                 }
    //             }
    //
    //             if(patientStatementClaim.patientPayments) {
    //                 for(const patientPayment of patientStatementClaim.patientPayments) {
    //                     const text = patientPayment.amount && patientPayment.amount.amount && parseInt(patientPayment.amount.amount.replace('.', '')) > 0 ? 'Patient Payment' : 'Patient Refund';
    //                     statementRows.push({
    //                         id: 'patientPayment' + patientPayment.id,
    //                         text: text,
    //                         date: patientPayment.paymentDate,
    //                         credit: patientPayment.amount ? patientPayment.amount.amount : '0.00'
    //                     });
    //                 }
    //             }
    //
    //             if(patientStatementClaim.payerPayments) {
    //                 for(const payerPayment of patientStatementClaim.payerPayments) {
    //                     statementRows.push({
    //                         id: 'payerPayment' + payerPayment.id,
    //                         text: payerPayment.insuranceName + ' Payment',
    //                         date: payerPayment.paymentDate,
    //                         credit: payerPayment.payerAmount ? payerPayment.payerAmount.amount : '0.00'
    //                     });
    //                     statementRows.push({
    //                         id: 'payerAdjustment' + payerPayment.id,
    //                         text: payerPayment.insuranceName + ' Adjustment',
    //                         date: payerPayment.paymentDate,
    //                         credit: payerPayment.adjustmentAmount ? payerPayment.adjustmentAmount.amount : '0.00'
    //                     });
    //                 }
    //             }
    //
    //             if(patientStatementClaim.balanceDue) {
    //                 statementRows.push({
    //                     id: 'balance' + patientStatementClaim.externalId,
    //                     text: 'Your Balance Due On This Claim ...',
    //                     balance: patientStatementClaim.balanceDue.amount
    //                 })
    //             }
    //         }
    //     }
    //
    //     return (
    //
    //             <S.StyledPageLayoutRow>
    //                 <table style={{border: '1px solid #000'}}>
    //                     <tr>
    //                         <td style={{border: '1px solid #000', verticalAlign: 'top'}}>
    //                             <div style={{fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase'}}>Patient Information</div>
    //                             {patientStatement.patient && (
    //                                 <div>{patientStatement.patient.first} {patientStatement.patient.middle} {patientStatement.patient.last}</div>)
    //                             }
    //                             {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
    //                                 patientStatement.patient.addresses[0].lines && patientStatement.patient.addresses[0].lines.length > 0 && (
    //                                 <div>{patientStatement.patient.addresses[0].lines[0]}</div>)
    //                             }
    //                             {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
    //                                 patientStatement.patient.addresses[0].lines && patientStatement.patient.addresses[0].lines.length > 1 && (
    //                                     <div>{patientStatement.patient.addresses[0].lines[1]}</div>)
    //                             }
    //                             {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
    //                                 (
    //                                     <div>{patientStatement.patient.addresses[0].city}, {patientStatement.patient.addresses[0].state} {patientStatement.patient.addresses[0].postal}</div>)
    //                             }
    //                         </td>
    //                         <td style={{border: '1px solid #000', verticalAlign: 'top'}}>
    //                             <div style={{fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase'}}>THIS IS A STATEMENT OF SERVICES RENDERED BY PHYSICIAN(S) WHO ARE MEMBERS OF:</div>
    //                             {patientStatement.billingProvider && patientStatement.billingProvider.organization && (
    //                                 <div>{patientStatement.billingProvider.organization}</div>)
    //                             }
    //                             {patientStatement.billingProvider && patientStatement.billingProvider.first && (
    //                                 <div>{patientStatement.billingProvider.first} {patientStatement.billingProvider.middle} {patientStatement.billingProvider.last}</div>)
    //                             }
    //                             {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
    //                                 patientStatement.billingProvider.addresses[0].lines && patientStatement.billingProvider.addresses[0].lines.length > 0 && (
    //                                     <div>{patientStatement.billingProvider.addresses[0].lines[0]}</div>)
    //                             }
    //                             {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
    //                                 patientStatement.billingProvider.addresses[0].lines && patientStatement.billingProvider.addresses[0].lines.length > 1 && (
    //                                     <div>{patientStatement.billingProvider.addresses[0].lines[1]}</div>)
    //                             }
    //                             {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
    //                                 (
    //                                     <div>{patientStatement.billingProvider.addresses[0].city}, {patientStatement.billingProvider.addresses[0].state} {patientStatement.billingProvider.addresses[0].postal}</div>)
    //                             }
    //                         </td>
    //                     </tr>
    //                     <tr>
    //                         <td colSpan={2} style={{border: '1px solid #000', verticalAlign: 'top'}}>
    //                             <table style={{width: '100%'}}>
    //                                 <thead style={{textAlign: 'left'}}>
    //                                     <th>Date</th>
    //                                     <th>Description</th>
    //                                     <th>Charge</th>
    //                                     <th>Credit</th>
    //                                     <th>Balance</th>
    //                                 </thead>
    //                                 <tbody>
    //                                 {statementRows.map(item => (
    //                                     <tr key={item.id}>
    //                                         <td>{item.date}</td>
    //                                         <td>{item.text}</td>
    //                                         <td>{item.charge}</td>
    //                                         <td>{item.credit}</td>
    //                                         <td>{item.balance}</td>
    //                                     </tr>
    //                                 ))}
    //                                 </tbody>
    //                             </table>
    //                         </td>
    //                     </tr>
    //                     <tr>
    //                         <td colSpan={2} style={{border: '1px solid #000', verticalAlign: 'top'}}>
    //                             <table style={{width: '100%'}}>
    //                                 <thead style={{fontWeight: 'bold', textTransform: 'uppercase'}}>
    //                                     <tr>
    //                                         <th>
    //                                             Current Date
    //                                         </th>
    //                                         <th>
    //                                             Patient
    //                                         </th>
    //                                         <th>
    //                                             Account #
    //                                         </th>
    //                                         <th></th>
    //                                         <th></th>
    //                                     </tr>
    //                                 </thead>
    //                                 <tbody>
    //                                     <tr>
    //                                         <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
    //                                             {this.getCurrentDate()}
    //                                         </td>
    //                                         <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
    //                                             {patientStatement.patient && (
    //                                                 <span>{patientStatement.patient.first} {patientStatement.patient.middle} {patientStatement.patient.last}</span>)
    //                                             }
    //                                         </td>
    //                                         <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
    //                                             {patientStatement.patient && (
    //                                                 <span>{patientStatement.patient.externalId}</span>)
    //                                             }
    //                                         </td>
    //                                         <td style={{fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center'}}>
    //                                             <span>PAY THIS AMOUNT</span>
    //                                         </td>
    //                                         <td style={{fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'left'}}>
    //                                             {patientStatement.balanceDue && (
    //                                                 <span>{patientStatement.balanceDue.amount}</span>)
    //                                             }
    //                                         </td>
    //                                     </tr>
    //                                 </tbody>
    //                             </table>
    //                         </td>
    //                     </tr>
    //                 </table>
    //             </S.StyledPageLayoutRow>
    //     );
    // }

    public render(): React.ReactElement {
        return this.state.patientStatement ? this.renderPage(this.state.printMode, this.state.patientStatement) : this.renderLoader();
    }
}


export const PatientStatement: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            const messagingContext = useContext(ProviderMessagingContext);
            const history = useHistory();
            const location = useLocation();
            const match = useRouteMatch<IMatchParams>();
            return (
                <PatientStatementPage
                    authContext={authContext}
                    dialogContext={dialogContext}
                    messagingContext={messagingContext}
                    rootStore={rootStore}
                    history={history}
                    location={location}
                    match={match}
                />
            );
        },
    ),
);